import { Button } from "@heroui/react";
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  GraduationCap,
  Menu,
  Moon,
  Sun,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, Suspense } from "react";
import { Outlet, useLocation, NavLink } from "react-router";
import { PageLoadingFallback } from "./PageLoadingFallback";
import { useAuth } from "../context/AuthContext";
import { fetchMenus, type BackendMenu } from "../services/api";
import { useActiveScope } from "../context/ScopeContext";
import { NotificationBell } from "./header/NotificationBell";
import { HelpHeaderLink } from "../features/help/components/HelpHeaderLink";
import { PersonaSwitcher } from "./header/PersonaSwitcher";
import { ScopeSwitcher } from "./header/ScopeSwitcher";
import { FamilyChildSwitcher } from "./header/FamilyChildSwitcher";
import { UserMenu } from "./header/UserMenu";
import { SidebarNav } from "./sidebar/SidebarNav";
import { Logo } from "./Logo";

const roleNames: Record<string, string> = {
  super_admin: "Super Admin",
  pimpinan: "Pimpinan Yayasan",
  admin_spmb: "Admin SPMB",
  admin_finance: "Admin Finance",
  admin_hr: "Admin HR",
  parent: "Orang Tua",
  employee: "Guru / Staf",
  observer_spmb: "Observer SPMB",
  referral: "Mitra Referral",
  REFERRAL: "Mitra Referral",
  referral_partner: "Mitra Referral",
  REFERRAL_PARTNER: "Mitra Referral",
};


export function AppShell() {
  const { user, hasPermission, activePersona, canSwitchPersona, switchPersona } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileNavRef = useRef<HTMLElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("wicend.erp.sidebar");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [theme, setTheme] = useState(
    () => localStorage.getItem("wicend.erp.theme") ?? "light",
  );
  const [menus, setMenus] = useState<BackendMenu[]>([]);
  // Unit and academic year live in ScopeContext so the header switcher, the
  // outgoing request headers, and the pages all read one source of truth.
  const { scopeKey } = useActiveScope();

  useEffect(() => {
    if (!user) return;
    fetchMenus().then(setMenus).catch(console.error);
  }, [user]);

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (mobileOpen) mobileNavRef.current?.focus();
  }, [mobileOpen]);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("wicend.erp.theme", theme);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem("wicend.erp.sidebar", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  const renderSidebar = (isMobile = false) => {
    const expanded = isMobile || sidebarOpen;

    return (
      <div className="flex h-full flex-col">
        <div
          className={`flex h-20 items-center border-b border-white/10 transition-[padding,justify-content] duration-300 ${
            expanded ? "justify-between px-5" : "justify-center px-2"
          }`}
        >
          <NavLink to="/app" title="Kembali ke Beranda" data-testid="shell-sidebar-home-link" className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-xl">
            <Logo compact={!expanded} theme="dark" />
          </NavLink>
          {isMobile && (
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              aria-label="Tutup menu"
              onPress={() => setMobileOpen(false)}
              data-testid="shell-mobile-menu-close-btn"
            >
              <X size={20} />
            </Button>
          )}
        </div>

        {isMobile && canSwitchPersona && (
          <div className="border-b border-white/10 px-4 py-3 bg-white/[0.03]">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-teal-200/60">
              Mode Peran
            </p>
            <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-white/5 p-1 border border-white/10">
              <button
                type="button"
                onClick={() => {
                  if (activePersona !== 'STAFF') {
                    setMobileOpen(false);
                    switchPersona('STAFF');
                  }
                }}
                data-testid="shell-mobile-persona-staff-btn"
                className={`flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-bold transition focus-visible:outline-2 focus-visible:outline-teal-400 ${
                  activePersona === 'STAFF'
                    ? 'bg-wicend-600 text-white shadow-sm'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <GraduationCap size={15} />
                <span>Guru / Staf</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (activePersona !== 'PARENT') {
                    setMobileOpen(false);
                    switchPersona('PARENT');
                  }
                }}
                data-testid="shell-mobile-persona-parent-btn"
                className={`flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-bold transition focus-visible:outline-2 focus-visible:outline-emerald-400 ${
                  activePersona === 'PARENT'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Users size={15} />
                <span>Orang Tua</span>
              </button>
            </div>
          </div>
        )}

        <SidebarNav
          menus={menus}
          hasPermission={hasPermission}
          expanded={expanded}
          onRequestExpand={() => setSidebarOpen(true)}
        />

        {isMobile && (
          <div className="mt-auto border-t border-white/10 p-3 bg-white/[0.02] flex items-center justify-between text-xs">
            <NavLink
              to={location.pathname.startsWith("/mitra") ? "/mitra/bantuan" : "/app/bantuan"}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-medium text-teal-100/90 hover:bg-white/10 hover:text-white"
              data-testid="shell-mobile-help-link"
            >
              <CircleHelp size={15} />
              <span>Bantuan</span>
            </NavLink>
            <button
              type="button"
              onClick={() => setTheme((value) => (value === "dark" ? "light" : "dark"))}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-medium text-teal-100/90 hover:bg-white/10 hover:text-white"
              data-testid="shell-mobile-theme-toggle-btn"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
              <span>{theme === "dark" ? "Mode Terang" : "Mode Gelap"}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 overflow-x-hidden">
      <a className="skip-link" href="#main-content" data-testid="shell-skip-link">Langsung ke konten utama</a>
      <aside
        data-testid="shell-sidebar"
        className={`fixed inset-y-0 left-0 z-40 hidden bg-wicend-950 text-white transition-[width] duration-300 ease-in-out lg:block ${
          sidebarOpen ? "w-72" : "w-20"
        }`}
      >
        {renderSidebar(false)}
        <button
          type="button"
          onClick={() => setSidebarOpen((prev: boolean) => !prev)}
          className="absolute -right-3.5 top-6 z-50 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-3 focus-visible:outline-wicend-400 focus-visible:outline-offset-2 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
          aria-label={sidebarOpen ? "Sembunyikan sidebar" : "Tampilkan sidebar"}
          data-testid="shell-sidebar-collapse-btn"
        >
          {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 cursor-default bg-slate-950/55 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label="Tutup menu navigasi"
            data-testid="shell-mobile-nav-backdrop-btn"
          />
          <aside ref={mobileNavRef} tabIndex={-1} aria-label="Navigasi utama" data-testid="shell-mobile-nav" className="relative h-full w-[85%] max-w-72 bg-wicend-950 text-white shadow-2xl">
            {renderSidebar(true)}
          </aside>
        </div>
      )}

      <div
        className={`transition-[padding] duration-300 ease-in-out ${sidebarOpen ? "lg:pl-72" : "lg:pl-20"}`}
      >
        <header data-testid="shell-topbar" className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/95 px-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95 sm:px-4 md:px-8">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button
              className="lg:hidden shrink-0"
              isIconOnly
              variant="ghost"
              aria-label="Buka menu"
              onPress={() => setMobileOpen(true)}
              data-testid="shell-mobile-menu-open-btn"
            >
              <Menu size={21} />
            </Button>
            <ScopeSwitcher />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 shrink-0">
            <PersonaSwitcher />
            <FamilyChildSwitcher />

            <Button
              className="hidden md:inline-flex"
              isIconOnly
              variant="ghost"
              aria-label="Ubah tema"
              onPress={() =>
                setTheme((value) => (value === "dark" ? "light" : "dark"))
              }
              data-testid="shell-theme-toggle-btn"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <div className="hidden md:inline-flex">
              <HelpHeaderLink
                to={location.pathname.startsWith("/mitra") ? "/mitra/bantuan" : "/app/bantuan"}
                testId="shell-help-link"
              />
            </div>
            <NotificationBell />
            <div className="hidden h-8 w-px bg-slate-200 dark:bg-slate-800 md:block" />
            <UserMenu
              roleLabel={roleNames[user?.role ?? ""] ?? "Pengguna"}
              theme={theme}
              onToggleTheme={() =>
                setTheme((value) => (value === "dark" ? "light" : "dark"))
              }
            />
          </div>
        </header>
        <main id="main-content" data-testid="shell-main-content" className="mx-auto max-w-[1680px] p-4 md:p-7" tabIndex={-1}>
          {/* Remounting on scope change re-runs every page's data effect, so a
              unit or year switch always refetches instead of showing stale rows. */}
          <div key={scopeKey}>
            <Suspense fallback={<PageLoadingFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
